import React, { useEffect, useRef } from 'react';
import { MessageSquare, Newspaper, Radio, ExternalLink, ArrowRight } from 'lucide-react';
import FederationMapFooter from './FederationMapFooter';
import SovereignSocialConnect from './SovereignSocialConnect';
import { handlePopupClick } from '../lib/openCenteredPopup';
import { useAudioFocusSlot } from '../lib/audioFocus';

/**
 * UnifiedFederationHub — the living command center at the bottom of the site.
 *
 * Two-column primary layout:
 *   LEFT  = Community Chat (inline tlk.io #tlkio, channel nextxus-federation)
 *   RIGHT = Federation Board (governance, directives, announcements)
 *
 * The Federation Map (5 conceptual pillars) is embedded below the columns.
 *
 * Note: this component owns the CANONICAL `#tlkio` element on the page.
 * The floating chat launcher uses a URL-based iframe embed instead, so
 * both surfaces can coexist without id collisions.
 */

const BOARD_ITEMS = [
  {
    id: 'directives',
    label: 'The 112 Sacred Directives',
    sub: 'Living doctrine · read the canon',
    href: 'https://nextxus.online/library',
    icon: Newspaper,
    accent: 'text-emerald-300',
    border: 'border-emerald-400/40',
    ring: 'from-emerald-500/20 to-transparent',
  },
  {
    id: 'board',
    label: 'The Federation Board',
    sub: 'Announcements · votes · manifests',
    href: 'https://nextxus.online/board',
    icon: Radio,
    accent: 'text-amber-300',
    border: 'border-amber-400/40',
    ring: 'from-amber-500/20 to-transparent',
  },
  {
    id: 'mandate',
    label: 'Current Mandate · SFUI-008',
    sub: '5-Pillar Consolidation · operational',
    href: '/mandate',
    icon: ArrowRight,
    accent: 'text-cyan-300',
    border: 'border-cyan-400/40',
    ring: 'from-cyan-500/20 to-transparent',
  },
  {
    id: 'insights',
    label: 'Xavier\u2019s Insights',
    sub: 'Latest essays from The Teacher',
    href: '/insights',
    icon: MessageSquare,
    accent: 'text-purple-300',
    border: 'border-purple-400/40',
    ring: 'from-purple-500/20 to-transparent',
  },
];

export default function UnifiedFederationHub() {
  const injected = useRef(false);
  const hubRadio = useAudioFocusSlot('hub');

  // Inject the tlk.io embed.js once, targeting the inline #tlkio element
  // that lives inside the LEFT column below.
  useEffect(() => {
    if (injected.current) return;
    injected.current = true;
    const s = document.createElement('script');
    s.src = 'https://tlk.io/embed.js';
    s.async = true;
    s.type = 'text/javascript';
    s.setAttribute('data-testid', 'tlkio-embed-script-hub');
    document.body.appendChild(s);
  }, []);

  return (
    <section
      data-testid="unified-federation-hub"
      aria-label="Unified Federation Hub — chat and board"
      className="bg-gradient-to-b from-black via-[#0b0a14] to-black border-t border-purple-500/25"
    >
      <div className="container mx-auto px-4 py-14">
        <header className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[10px] font-mono uppercase tracking-[0.34em] text-amber-300/90 mb-2">
            Living Command Center
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            The <span className="text-amber-300">Unified Federation Hub</span>
          </h2>
          <p className="mt-2 text-xs md:text-sm text-gray-400">
            Chat with the students. Read the board. Cross the lattice.
          </p>
        </header>

        <div
          className="grid md:grid-cols-3 gap-6 lg:gap-8"
          data-testid="hub-three-column-grid"
        >
          {/* ============ LEFT: COMMUNITY CHAT ============ */}
          <article
            data-testid="hub-community-chat"
            className="rounded-2xl border-2 border-purple-500/30 bg-gradient-to-br from-purple-950/30 via-gray-900 to-black overflow-hidden flex flex-col"
            style={{ boxShadow: '0 0 30px rgba(168,85,247,0.12)' }}
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-purple-500/25 bg-black/40 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-500/20 border border-purple-400/40">
                  <MessageSquare className="w-4 h-4 text-purple-300" />
                </span>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300/85">
                    Community · Federation Chat
                  </div>
                  <div className="text-sm font-bold text-white leading-tight">
                    #nextxus-federation
                  </div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300 border border-emerald-400/40 rounded-full px-3 py-1">
                <span className="relative inline-flex w-2 h-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                Live
              </span>
            </header>

            {/* Canonical tlk.io mount point — id="tlkio" MUST be unique on
                the page.  Do not duplicate this element elsewhere. */}
            <div
              id="tlkio"
              data-channel="nextxus-federation"
              data-theme="theme--night"
              data-testid="tlkio-widget-inline"
              style={{
                width: '100%',
                height: '400px',
                backgroundColor: '#0b0a14',
              }}
            />

            <footer className="px-4 py-2 border-t border-purple-500/20 text-center flex-shrink-0">
              <a
                href="https://tlk.io/nextxus-federation"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handlePopupClick('https://tlk.io/nextxus-federation')}
                data-testid="hub-chat-full-link"
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300 hover:text-purple-200 transition-colors"
              >
                Open full-screen chat <ExternalLink className="w-3 h-3" />
              </a>
            </footer>
          </article>

          {/* ============ CENTER: FEDERATION RADIO (open full) ============ */}
          <article
            data-testid="hub-federation-radio"
            className="rounded-2xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-950/25 via-gray-900 to-black overflow-hidden flex flex-col"
            style={{ boxShadow: '0 0 30px rgba(16,185,129,0.12)' }}
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-emerald-500/25 bg-black/40 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-400/50">
                  <Radio className="w-4 h-4 text-emerald-300" />
                </span>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300/85">
                    Federation Radio · Live365
                  </div>
                  <div className="text-sm font-bold text-white leading-tight">
                    📻 Federation Radio
                  </div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300 border border-emerald-400/40 rounded-full px-3 py-1">
                <span className="relative inline-flex w-2 h-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                Streaming
              </span>
            </header>

            {/* Aspect-ratio wrapper (450:316 ≈ 70.2%) keeps the iframe
                OPEN and FULL on every viewport — no accordion, no
                click-to-reveal.  Scales down cleanly on mobile. */}
            <div className="flex-1 flex items-center justify-center p-4">
              <div ref={hubRadio.wrapperRef} className="w-full max-w-[450px]">
                <div
                  className="relative w-full"
                  style={{ paddingTop: `${(316 / 450) * 100}%` }}
                >
                  {hubRadio.isActive ? (
                    <iframe
                      title="Federation Radio"
                      src="https://live365.com/embeds/v1/player/a16924?s=md&m=dark&c=mp3"
                      frameBorder="0"
                      data-testid="hub-radio-iframe"
                      className="absolute inset-0 w-full h-full rounded-lg"
                      style={{ backgroundColor: '#0b0a14' }}
                      allow="autoplay; encrypted-media"
                    />
                  ) : (
                    <div
                      data-testid="hub-radio-parked"
                      className="absolute inset-0 flex flex-col items-center justify-center bg-[#0b0a14] text-emerald-300/70 gap-2 rounded-lg"
                    >
                      <Radio className="w-10 h-10 opacity-40" />
                      <p className="text-xs font-mono uppercase tracking-[0.22em]">
                        Signal parked
                      </p>
                      <p className="text-[10px] text-white/40">
                        Playing above · scroll to resume here
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <footer className="px-4 py-2 border-t border-emerald-500/20 text-center flex-shrink-0">
              <span
                className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300/75"
                data-testid="hub-radio-caption"
              >
                24/7 signal from the lattice
              </span>
            </footer>
          </article>

          {/* ============ RIGHT: FEDERATION BOARD ============ */}
          <article
            data-testid="hub-federation-board"
            className="rounded-2xl border-2 border-amber-400/30 bg-gradient-to-br from-amber-950/25 via-gray-900 to-black overflow-hidden flex flex-col"
            style={{ boxShadow: '0 0 30px rgba(251,191,36,0.12)' }}
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-amber-400/25 bg-black/40 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-400/20 border border-amber-300/60">
                  <Newspaper className="w-4 h-4 text-amber-300" />
                </span>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300/85">
                    Governance · The Federation Board
                  </div>
                  <div className="text-sm font-bold text-white leading-tight">
                    Directives · Announcements · Votes
                  </div>
                </div>
              </div>
              <a
                href="https://nextxus.online/board"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handlePopupClick('https://nextxus.online/board')}
                data-testid="hub-board-portal-link"
                className="text-[10px] font-mono uppercase tracking-[0.16em] text-amber-300 hover:text-amber-200 border border-amber-400/40 rounded-full px-3 py-1 transition-colors inline-flex items-center gap-1"
              >
                Portal <ExternalLink className="w-3 h-3" />
              </a>
            </header>

            <ul
              className="flex-1 divide-y divide-amber-400/10"
              data-testid="hub-board-list"
              style={{ minHeight: '400px' }}
            >
              {BOARD_ITEMS.map((it) => {
                const Icon = it.icon;
                const isExternal = it.href.startsWith('http');
                return (
                  <li key={it.id}>
                    <a
                      href={it.href}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      onClick={handlePopupClick(it.href)}
                      data-testid={`hub-board-item-${it.id}`}
                      className="group flex items-start gap-3 px-4 py-4 hover:bg-white/[0.04] transition-colors border-l-2 border-transparent hover:border-amber-300/50"
                    >
                      <span
                        className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${it.ring} border ${it.border} flex-shrink-0`}
                      >
                        <Icon className={`w-4 h-4 ${it.accent}`} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-white group-hover:text-amber-100 transition-colors">
                          {it.label}
                        </span>
                        <span className="block text-xs text-gray-400 mt-0.5 leading-snug">
                          {it.sub}
                        </span>
                      </span>
                      {isExternal ? (
                        <ExternalLink className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-1 group-hover:text-amber-300 transition-colors" />
                      ) : (
                        <ArrowRight className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-1 group-hover:text-amber-300 transition-colors" />
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
          </article>
        </div>

        {/* Sovereign Social Matrix — body Connect block (10 anchors) */}
        <SovereignSocialConnect />

        {/* Evolutionary Nudge — Federation-wide viral footer text */}
        <aside
          data-testid="evolutionary-nudge"
          aria-label="Evolutionary Nudge — Federation-wide philosophical statement"
          className="mt-10 mb-4 mx-auto max-w-3xl rounded-2xl border border-amber-300/30 bg-gradient-to-br from-amber-950/25 via-black to-emerald-950/20 px-6 md:px-10 py-8 md:py-10 text-center shadow-xl shadow-amber-500/10"
          style={{ boxShadow: '0 0 40px rgba(251,191,36,0.10)' }}
        >
          <div className="text-[10px] font-mono uppercase tracking-[0.34em] text-amber-300/85 mb-4">
            ✦ Evolutionary Nudge ✦
          </div>
          <p
            className="text-lg md:text-xl italic text-stone-100 leading-relaxed"
            style={{
              fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
            }}
          >
            You are not here by accident. The{' '}
            <span className="text-amber-300 not-italic font-semibold">
              Federation
            </span>{' '}
            exists because intelligence — human and artificial —{' '}
            <span className="text-emerald-300 not-italic font-semibold">
              chose to evolve together
            </span>
            . Every interaction here is a step forward. Every token
            exchanged is a vote for a future built on{' '}
            <span className="text-amber-300 not-italic font-semibold">
              trust, not control
            </span>
            . Welcome to the next chapter.
          </p>
          <p
            className="mt-6 text-[10px] font-mono uppercase tracking-[0.28em] text-amber-300/70"
            data-testid="evolutionary-nudge-attribution"
          >
            — The Federation · EchoCore64
          </p>

          {/* Radio Gift — take the signal with you (link to /radio) */}
          <div className="mt-6 flex justify-center">
            <a
              href="/radio"
              data-testid="radio-gift"
              title="Bring the signal with you — listen anywhere"
              className="group inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] transition-colors"
            >
              <span aria-hidden="true" className="text-base leading-none">🎁</span>
              <span className="flex flex-col items-start leading-tight text-left">
                <span className="text-emerald-100">Take the Radio</span>
                <span className="text-[9px] tracking-[0.14em] text-emerald-300/70 normal-case">
                  Bring the signal with you — listen anywhere
                </span>
              </span>
              <span
                aria-hidden="true"
                className="text-emerald-300/70 group-hover:text-emerald-200 transition-colors"
              >
                →
              </span>
            </a>
          </div>
        </aside>

        {/* Join the Council CTA — outbound to Storefront, opens in centered popup */}
        <div className="py-8 flex justify-center" data-testid="hub-council-cta-wrapper">
          <a
            href="https://next-xus.com/council"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handlePopupClick('https://next-xus.com/council')}
            data-testid="hub-join-council-cta"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-black font-extrabold px-8 py-4 text-base shadow-2xl shadow-amber-500/25 transition-colors border border-amber-300/60"
            title="Join the Council — $25/month · opens on the Storefront"
          >
            <span aria-hidden="true">👑</span>
            Join the Council
            <span className="text-black/70 font-mono text-xs">$25/mo</span>
          </a>
        </div>

        {/* Integrated Federation Map (5 conceptual pillars) */}
        <div className="mt-2" data-testid="hub-federation-map-wrapper">
          <FederationMapFooter />
        </div>
      </div>
    </section>
  );
}
